import { ColorSelector, IconName } from '@minds/minds-react-sdk';

/* eslint-disable @typescript-eslint/naming-convention */
export enum RecommendedActions {
    acceptWithRisk = 'accept_with_risk',
    accept = 'accept',
    reject = 'reject',
}

export enum Actions {
    verification = 'verification',
    enrollment = 'enrollment',
}

export enum RequestSources {
    trialWeb = 'TRIAL_WEB',
    api = 'API',
    sdkAndroid = 'SDK_ANDROID',
    sdkIos = 'SDK_IOS',
    ura = 'URA',
    chatbot = 'CHATBOT',
}

export enum FlagTypes {
    phoneBlacklist = 'phone_blacklist',
    speakerBlacklist = 'speaker_blacklist',
    graph = 'graph',
    quarantinePhoneBlocklist = 'quarantine_phone_blocklist',
    quarantineSpeakerBlocklist = 'quarantine_speaker_blocklist',
    voiceDifferent = 'voice_different',
    spoofReplayAttack = 'spoof_replay_attack',
    spoofDeepFake = 'spoof_deep_fake',
    sentenceMismatch = 'sentence_mismatch',
    suspiciousPhoneNumber = 'suspicious_phone_number',
    anonymousPhoneNumber = 'anonymous_phone_number',
    invalidPhoneNumber = 'invalid_phone_number'
}

export enum FlagSubTypes {
    phoneMultipleDocumentsCalls = 'phone_multiple_documents_calls',
    documentFromMultiplePhones = 'document_from_multiple_phones',
    documentFromMultipleDDDs = 'document_from_multiple_ddds',
    defaultGraphAlert = 'default_graph_alert',
}

export function normalizeFlagType(type: FlagTypes) {
    return type === FlagTypes.graph ? 'suspicious_behaviour' : type;
}

export function getFlagStatusText(
    hasFlagResult: boolean,
    isFraud: boolean,
    isUnderAnalysis: boolean,
    flagResultType?: FlagResults,
): FlagResults {
    const validFlagResultType =
        flagResultType && Object.values(FlagResults).includes(flagResultType)
            ? flagResultType
            : undefined;
    if (validFlagResultType) return validFlagResultType;
    if (hasFlagResult || flagResultType) return isFraud ? FlagResults.fraud : FlagResults.noFraud;
    if (isUnderAnalysis) return FlagResults.underAnalysis;
    return FlagResults.waitingAnalysis;
}

export function isPhoneFlag(value?: FlagTypes) {
    return (
        value &&
        [FlagTypes.phoneBlacklist, FlagTypes.quarantinePhoneBlocklist].includes(
            value,
        )
    );
}

export function isVoiceFlag(value?: FlagTypes) {
    return (
        value &&
        [
            FlagTypes.speakerBlacklist,
            FlagTypes.quarantineSpeakerBlocklist,
        ].includes(value)
    );
}

export function getDisplayReasons(reasons?: Reasons[]) {
    const filteredReasons =
        reasons?.filter((reason) => !reason.includes('silenced')) ?? [];
    return [...new Set(filteredReasons)];
}

export enum DocumentTypes {
    cpf = 'cpf',
    generic = 'generic',
    ssn = 'ssn',
}

export enum PriorityLevels {
    high = 'high',
    medium = 'medium',
    low = 'low',
    undefined = 'undefined',
}

export enum FlagResults {
    waitingAnalysis = 'waiting_analysis',
    underAnalysis = 'under_analysis',
    fraud = 'fraud',
    noFraud = 'no_fraud',
    fraudSuspect = 'fraud_suspect',
    portabilityAttack = 'portability_attack',
    portabilityAttackSuspect = 'portability_attack_suspect',
    archivedWithoutAnalysis = 'archived_without_analysis',
}

export enum FlagResultsFinishAnalysis {
    fraud = 'fraud',
    noFraud = 'no_fraud',
    fraudSuspect = 'fraud_suspect',
    portabilityAttack = 'portability_attack',
    portabilityAttackSuspect = 'portability_attack_suspect',
    archivedWithoutAnalysis = 'archived_without_analysis',
}

export enum FlagResultsFinishAnalysisId {
    fraud = 1,
    noFraud = 2,
    fraudSuspect = 3,
    portabilityAttack = 4,
    portabilityAttackSuspect = 5,
    archivedWithoutAnalysis = 6,
}

export const FlagResultsFinishAnalysisIdMap: Record<
    FlagResultsFinishAnalysisId,
    FlagResultsFinishAnalysis
> = {
    [FlagResultsFinishAnalysisId.fraud]: FlagResultsFinishAnalysis.fraud,
    [FlagResultsFinishAnalysisId.noFraud]: FlagResultsFinishAnalysis.noFraud,
    [FlagResultsFinishAnalysisId.fraudSuspect]:
        FlagResultsFinishAnalysis.fraudSuspect,
    [FlagResultsFinishAnalysisId.portabilityAttack]:
        FlagResultsFinishAnalysis.portabilityAttack,
    [FlagResultsFinishAnalysisId.portabilityAttackSuspect]:
        FlagResultsFinishAnalysis.portabilityAttackSuspect,
    [FlagResultsFinishAnalysisId.archivedWithoutAnalysis]:
        FlagResultsFinishAnalysis.archivedWithoutAnalysis,
};

export const BMG_FLAG_RESULTS = [
    FlagResults.waitingAnalysis,
    FlagResults.fraud,
    FlagResults.noFraud,
];

export const FlagResultsColorMap: Record<
    FlagResults,
    [ColorSelector, ColorSelector]
> = {
    waiting_analysis: ['orange-300', 'orange-950'],
    under_analysis: ['gray-200', 'gray-950'],
    fraud: ['red-300', 'red-600'],
    no_fraud: ['blue-50', 'blue-550'],
    fraud_suspect: ['red-300', 'red-600'],
    portability_attack: ['red-300', 'red-600'],
    portability_attack_suspect: ['red-300', 'red-600'],
    archived_without_analysis: ['gray-200', 'gray-950'],
};

export const FlagResultsIconMap: Record<FlagResults, IconName> = {
    waiting_analysis: 'i-ph-clock',
    under_analysis: 'i-ph-lock-simple',
    fraud: 'i-ph-warning',
    no_fraud: 'i-ph-user',
    fraud_suspect: 'i-ph-warning',
    portability_attack: 'i-ph-warning',
    portability_attack_suspect: 'i-ph-warning',
    archived_without_analysis: 'i-ph-archive',
};

export enum Reasons {
    voiceMatch = 'voice_match',
    voiceDifferent = 'voice_different',
    confidenceLow = 'confidence_low',
    voiceMatchConfidenceLow = 'voice_match_confidence_low',
    phoneFlag = 'phone_flag',
    voiceFlag = 'voice_flag',
    anonymousPhoneNumber = 'anonymous_phone_number',
    errorAntiFraud = 'error_anti_fraud',
    spoof = 'spoof',
    errorAntispoofing = 'error_antispoofing',
    enrollmentSuccess = 'enrollment_success',
    silencedPhoneFlag = 'silenced_phone_flag',
    silencedVoiceFlag = 'silenced_voice_flag',
    suspiciousBehaviourFlag = 'suspicious_behaviour_flag',
    sentenceMismatch = 'sentence_mismatch',
    silencedSuspiciousBehaviourFlag = 'silenced_suspicious_behaviour_flag',
    silencedSuspiciousPhoneNumber = 'silenced_suspicious_phone_number',
    suspiciousPhoneNumber = 'suspicious_phone_number',
    foreignPhoneNumber = 'foreign_phone_number',
    invalidPhoneNumber = 'invalid_phone_number'
}

export const reasonsWeightMap: Record<Reasons, number> = {
    anonymous_phone_number: 3.7,
    phone_flag: 3.6,
    voice_flag: 3.5,
    suspicious_behaviour_flag: 3.4,
    silenced_suspicious_behaviour_flag: 3.15,
    silenced_suspicious_phone_number: 3.14,
    suspicious_phone_number: 3.13,
    silenced_phone_flag: 3.1,
    silenced_voice_flag: 3,
    spoof: 2.2,
    sentence_mismatch: 2.1,
    voice_different: 2,
    voice_match: 1.3,
    enrollment_success: 1.2,
    confidence_low: 1.1,
    voice_match_confidence_low: 1.1,
    error_antispoofing: 1,
    error_anti_fraud: 1,
    foreign_phone_number: 3.8,
    invalid_phone_number: 3.9,
};

export const orderedReasonsWeightMap = Object.entries(reasonsWeightMap)
    .sort((a, b) => b[1] - a[1])
    .map((x) => x[0] as Reasons);

export enum Confidence {
    high = 'high',
    medium = 'medium',
    low = 'low',
}

export enum MatchResults {
    match = 'match',
    different = 'different',
}

export enum RequestStatus {
    alreadyEnrolled = 'already_enrolled',
    alreadyRan = 'already_ran',
    error = 'error',
    pending = 'pending',
    processing = 'processing',
    ok = 'ok',
    invalidLength = 'invalid_length',
    audioTooLarge = 'audio_too_large',
    customerNotEnrolled = 'customer_not_enrolled',
    customerAlreadyEnrolledAndCertified = 'customer_already_enrolled_and_certified',
    customerNotFoundToPerformVerification = 'customer_not_found_to_perform_verification',
    internalRequest = 'internal_request',
    activeEnrollmentFound = 'active_enrollment_found',
    invalidDocumentType = 'invalperson_idument_type',
    invalidDocument = 'invalperson_idument',
    audioAlreadyUsed = 'audio_already_used',
    invalidPhoneNumber = 'invalid_phone_number',
    invalidAudio = 'invalid_audio',
}
